class BanzrcGtConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.section = this.closest('.shopify-section');
    this.rows = Array.from(this.querySelectorAll('[data-variant-row]')).map((row) => ({
      id: row.dataset.variantId,
      color: row.dataset.color,
      power: row.dataset.power,
      available: row.dataset.available === 'true',
      price: row.dataset.price,
      inventory: Number(row.dataset.inventory || 0),
    }));
    this.selectedColor = null;
    this.selectedPower = null;
    this.feedbackTimer = null;
    this.previewVariantId = null;

    this.querySelectorAll('[data-color]').forEach((button) => {
      if (!button.matches('button')) return;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.cancelPendingSync();
        this.selectedColor = button.dataset.color;
        this.render();
        if (!this.selectedPower) this.previewNativeColor(this.selectedColor);
      });
    });

    this.querySelectorAll('[data-power]').forEach((button) => {
      if (!button.matches('button')) return;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.cancelPendingSync();
        this.selectedPower = button.dataset.power;
        this.render();
      });
    });

    const variantFromUrl = new URL(window.location.href).searchParams.get('variant');
    const linkedVariant = this.rows.find((row) => row.id === variantFromUrl);
    if (linkedVariant) {
      this.selectedColor = linkedVariant.color;
      this.selectedPower = linkedVariant.power;
    }

    this.observePurchaseFeedback();
    this.render();
  }

  get selectedVariant() {
    if (!this.selectedColor || !this.selectedPower) return null;
    return this.rows.find((row) => row.color === this.selectedColor && row.power === this.selectedPower) || null;
  }

  render() {
    const variant = this.selectedVariant;
    const ready = Boolean(variant && variant.available);

    this.querySelectorAll('button[data-color]').forEach((button) => {
      const color = button.dataset.color;
      const possible = this.rows.some(
        (row) => row.color === color && (!this.selectedPower || row.power === this.selectedPower) && row.available
      );
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(color === this.selectedColor));
    });

    this.querySelectorAll('button[data-power]').forEach((button) => {
      const power = button.dataset.power;
      const possible = this.rows.some(
        (row) => row.power === power && (!this.selectedColor || row.color === this.selectedColor) && row.available
      );
      button.disabled = !possible;
      button.setAttribute('aria-pressed', String(power === this.selectedPower));
    });

    const colorValue = this.querySelector('[data-color-value]');
    const powerValue = this.querySelector('[data-power-value]');
    if (colorValue) colorValue.textContent = this.selectedColor || '';
    if (powerValue) powerValue.textContent = this.selectedPower || '';
    this.dataset.selectionComplete = String(ready);

    this.updateSelectionMessage(variant);
    this.updateSystemDetails(variant);
    this.updateSceneGallery();
    if (ready) {
      this.selectNativeVariant(variant);
    } else {
      this.syncVariantInput(null);
    }
    this.updatePurchaseControls(ready);
  }

  updateSelectionMessage(variant) {
    const message = this.querySelector('[data-selection-message]');
    if (!message) return;
    const copy = (key, fallback) => this.getAttribute(`data-${key}`) || fallback;
    if (!this.selectedColor && !this.selectedPower) {
      message.textContent = copy('select-color-power', 'Choose a color and power system.');
    } else if (!this.selectedColor) {
      message.textContent = copy('select-color', 'Choose a color.');
    } else if (!this.selectedPower) {
      message.textContent = copy('select-power', 'Choose a power system.');
    } else if (!variant || !variant.available) {
      message.textContent = copy('unavailable', 'This configuration is not available.');
    } else {
      message.textContent = `${variant.color} / ${variant.power} ${copy('selected-suffix', 'selected.')}`;
    }
  }

  updateSystemDetails(variant) {
    const power = this.selectedPower;
    const systemKey = power ? power.toLowerCase() : null;
    const setText = (selector, value) => {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    };

    setText('[data-price]', variant ? variant.price : this.getAttribute('data-choose-configuration') || 'Choose configuration');
    setText(
      '[data-inventory]',
      variant
        ? variant.available
          ? `${this.getAttribute('data-in-stock') || 'In stock'} — ${variant.inventory} available`
          : this.getAttribute('data-out-of-stock') || 'Out of stock'
        : this.getAttribute('data-choose-configuration') || 'Choose configuration'
    );
    const selectPower = this.getAttribute('data-select-power') || 'Choose power system';
    setText('[data-motor]', systemKey ? this.getAttribute(`data-motor-${systemKey}`) : selectPower);
    setText('[data-esc]', systemKey ? this.getAttribute(`data-esc-${systemKey}`) : selectPower);
    setText('[data-servo]', systemKey ? this.getAttribute(`data-servo-${systemKey}`) : selectPower);
    setText(
      '[data-battery-detail]',
      systemKey ? this.getAttribute(`data-battery-${systemKey}`) : this.getAttribute('data-battery-prompt') || 'Select 6S or 8S to confirm the compatible voltage range.'
    );
  }

  selectNativeVariant(variant) {
    this.cancelPendingSync();
    const input = this.section?.querySelector(`variant-picker input[data-variant-id="${CSS.escape(variant.id)}"]`);
    if (input && !input.checked) input.click();
    this.syncGeneration = (this.syncGeneration || 0) + 1;
    const generation = this.syncGeneration;
    this.syncVariantInput(variant);
    this.syncTimers = [300, 800, 1800, 3000].map((delay) => window.setTimeout(() => {
        if (generation === this.syncGeneration && this.selectedVariant?.id === variant.id) {
          this.syncVariantInput(variant);
        }
      }, delay));
  }

  previewNativeColor(color) {
    this.cancelPendingSync();
    const previewVariant =
      this.rows.find((row) => row.color === color && row.power === '6S' && row.available) ||
      this.rows.find((row) => row.color === color && row.available);
    if (!previewVariant) return;

    const input = this.section?.querySelector(
      `variant-picker input[data-variant-id="${CSS.escape(previewVariant.id)}"]`
    );
    if (!input || input.checked) return;
    this.previewVariantId = previewVariant.id;
    input.click();

    this.previewTimer = window.setTimeout(() => {
      if (this.selectedPower || this.previewVariantId !== previewVariant.id) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') === previewVariant.id) {
        url.searchParams.delete('variant');
        window.history.replaceState({}, '', url);
      }
      this.syncVariantInput(null);
      this.updatePurchaseControls(false);
    }, 1200);
  }

  cancelPendingSync() {
    this.syncGeneration = (this.syncGeneration || 0) + 1;
    this.syncTimers?.forEach((timer) => window.clearTimeout(timer));
    this.syncTimers = [];
    if (this.previewTimer) window.clearTimeout(this.previewTimer);
    this.previewTimer = null;
    this.previewVariantId = null;
  }

  updateSceneGallery() {
    const sceneGallery = this.section?.querySelector('[data-banzrc-scene-gallery]');
    const source = [...(sceneGallery?.querySelectorAll('[data-scene-source]') || [])].find(
      (entry) => entry.dataset.sceneColor === this.selectedColor
    );
    const image = sceneGallery?.querySelector('[data-scene-image]');
    const label = sceneGallery?.querySelector('[data-scene-label]');
    if (!sceneGallery || !source || !image || !label) return;

    const nextSrc = source.dataset.sceneSrc;
    const nextAlt = source.dataset.sceneAlt;
    const nextLabel = `${this.selectedColor.toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())} color story`;

    if (image.getAttribute('src') !== nextSrc) image.src = nextSrc;
    if (image.alt !== nextAlt) image.alt = nextAlt;
    if (label.textContent !== nextLabel) label.textContent = nextLabel;
  }

  syncVariantInput(variant) {
    const input = this.section?.querySelector('product-form-component input[name="id"]');
    if (!input) return;
    input.value = variant?.id || '';
    if (variant?.id) {
      input.setAttribute('value', variant.id);
    } else {
      input.removeAttribute('value');
    }
  }

  updatePurchaseControls(enabled) {
    const button = this.section?.querySelector('add-to-cart-component button');
    const accelerated = this.section?.querySelector('.accelerated-checkout-block');
    if (button) {
      button.disabled = !enabled;
      button.setAttribute('aria-disabled', String(!enabled));
    }
    if (accelerated) accelerated.hidden = !enabled;
  }

  observePurchaseFeedback() {
    if (!this.section) return;
    this.ensureVariantBeforePurchase = () => {
      const variant = this.selectedVariant;
      if (variant?.available) this.syncVariantInput(variant);
    };
    this.section.addEventListener('pointerdown', this.ensureVariantBeforePurchase, true);
    this.section.addEventListener('submit', this.ensureVariantBeforePurchase, true);
    this.observer = new MutationObserver((mutations) => {
      const variant = this.selectedVariant;
      if (this.dataset.selectionComplete === 'true' && variant?.available) {
        this.syncVariantInput(variant);
      }
      this.updatePurchaseControls(this.dataset.selectionComplete === 'true');
      const wasAdded = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-added' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.dataset.added === 'true'
      );
      if (wasAdded) this.showCartFeedback();
    });
    this.observer.observe(this.section, { subtree: true, attributes: true, attributeFilter: ['data-added'] });
  }

  showCartFeedback() {
    const feedback = this.querySelector('[data-cart-feedback]');
    if (!feedback) return;
    feedback.hidden = false;
    window.clearTimeout(this.feedbackTimer);
    this.feedbackTimer = window.setTimeout(() => {
      feedback.hidden = true;
    }, 5000);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.section && this.ensureVariantBeforePurchase) {
      this.section.removeEventListener('pointerdown', this.ensureVariantBeforePurchase, true);
      this.section.removeEventListener('submit', this.ensureVariantBeforePurchase, true);
    }
    window.clearTimeout(this.feedbackTimer);
    this.cancelPendingSync();
  }
}

if (!customElements.get('banzrc-gt-configurator')) {
  customElements.define('banzrc-gt-configurator', BanzrcGtConfigurator);
}
